/**
 * CommunityUnreadPill — bottom-of-list overlay for the community wall that
 * doubles as:
 *   1. An unread-summary pill when messages arrived while the user was scrolled
 *      away from the bottom (or the tab was hidden). Shows a preview of the most
 *      recent unread item and, when tapped, scrolls the list back to the top
 *      (newest) and flushes the unread bucket.
 *   2. A round "scroll-down" button when there are no unread items but the user
 *      is scrolled up.
 *
 * Flutter source of truth:
 *  - `unreadMessageWidget` (`community_screen.dart:1130-1210`) — the tappable
 *    pill, visible while `_unreadMessageReceived && _unreadListItems.isNotEmpty`;
 *    tap scrolls to index 0 and clears `_unreadMessageReceived`
 *    (`community_screen.dart:1166-1173`).
 *  - `scrolledUpWidget` (`community_screen.dart:1212-1238`) — the scroll-down
 *    button, visible while `!_unreadMessageReceived && _isScrolledUp`; tap calls
 *    `_scrollDown` (`community_screen.dart:845-853`).
 *
 * Modeled on the chat `UnreadBanner` (`@/presentation/feature/chat/components/
 * UnreadBanner`) — same single-call-site contract conflating both roles — but
 * carries community-scoped `data-qa-*` ids so the qa-tester scopes to the
 * community surface. The scroll-down branch reuses the shared
 * `ScrolledUpButton`.
 *
 * Visibility predicate (Flutter `community_screen.dart:1131`, `:1221`):
 *   pill   → `unreadBannerVisible && unreadPreview != null`
 *   button → `!unreadBannerVisible && isScrolledUp`
 * When both are false this renders `null` (the caller does not gate the mount).
 *
 * Deviations from Flutter UI (allowed per `presentation.md`) match the chat
 * `UnreadBanner`: the page owns vertical placement (Flutter's bottom padding
 * lives on the page parent), the pill uses theme tokens (`theme.secondary` /
 * `theme.text.primary`) for light/dark parity, and the dynamic short/long
 * horizontal padding (`community_screen.dart:1188-1189`) collapses to a single
 * `responsive.spacing.base` handled by flex centering.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { ScrolledUpButton } from '@/presentation/feature/chat/components/ScrolledUpButton';

export interface CommunityUnreadPillProps {
  /**
   * Resolved preview string for the newest unread item, or `null` when there
   * are no unread items. Text messages preview the raw body; non-text branches
   * use the Task-8 `community.unread.*` templates. Built by the host page.
   */
  unreadPreview: string | null;
  /**
   * Transient flag: `true` for ~3 s after every message-while-scrolled-away
   * arrival (`useCommunity.unreadBannerVisible`, Flutter `_unreadMessageReceived`).
   * When `false` the pill is skipped even if `unreadPreview` is non-null — the
   * surface falls back to the scroll-down button (or `null`).
   */
  unreadBannerVisible: boolean;
  /**
   * True iff the user is scrolled away from the bottom of the list
   * (`useCommunity.isScrolledUp`, Flutter `_isScrolledUp`). Drives the
   * scroll-down button fallback.
   */
  isScrolledUp: boolean;
  /**
   * Invoked when either branch is tapped. The host wires this to scroll the
   * list to the top (newest) and flush the unread bucket — Flutter
   * `_itemScrollController.scrollTo(index: 0, …)` + `_scrollDown`
   * (`community_screen.dart:1167-1172`, `:845-853`).
   */
  onScrollDown: () => void;
  /**
   * Current unread-item count, surfaced as `data-qa-value` on the pill so the
   * qa-tester can read the count rather than parse the preview text. Test
   * affordance only — does not affect rendering.
   */
  unreadCount?: number;
}

export const CommunityUnreadPill: React.FC<CommunityUnreadPillProps> = ({
  unreadPreview,
  unreadBannerVisible,
  isScrolledUp,
  onScrollDown,
  unreadCount,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  // Pill renders only while the 3 s `unreadBannerVisible` window is open AND
  // there is a preview to show; otherwise it degrades to the scroll-down button
  // (visible whenever the user is scrolled away). Mirrors Flutter's two
  // disjoint widgets (`community_screen.dart:1131`, `:1221`).
  const showUnreadPill = unreadBannerVisible && unreadPreview !== null;
  const showScrollDownButton = !showUnreadPill && isScrolledUp;

  if (!showUnreadPill && !showScrollDownButton) {
    return null;
  }

  return (
    <div
      style={{
        // Flutter centers the row horizontally; the page owns bottom placement.
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        // Don't intercept clicks on surrounding chrome; the inner element
        // re-enables pointer events on its own surface.
        pointerEvents: 'none',
      }}
    >
      {showUnreadPill ? (
        <button
          type="button"
          {...qaAttr({ id: 'community-unread-pill', value: unreadCount })}
          onClick={onScrollDown}
          style={{
            // Flutter `ConstrainedBox(maxWidth: width / 2)`
            // (`community_screen.dart:1177`).
            maxWidth: '50vw',
            backgroundColor: theme.secondary,
            color: theme.text.primary,
            // Flutter `BorderRadius.all(Radius.circular(14.0))`
            // (`community_screen.dart:1182`). Closest token is `lg` (16).
            borderRadius: responsive.borderRadius.lg,
            paddingTop: responsive.spacing.base,
            paddingBottom: responsive.spacing.base,
            paddingLeft: responsive.spacing.base,
            paddingRight: responsive.spacing.base,
            border: 'none',
            textAlign: 'center',
            fontSize: responsive.font.lg,
            fontWeight: responsive.fontWeight.regular,
            cursor: 'pointer',
            // Two-line clamp mirrors Flutter `AutoSizeText(maxLines: 2,
            // overflow: TextOverflow.ellipsis)` (`community_screen.dart:1193-1195`).
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            pointerEvents: 'auto',
          }}
        >
          {unreadPreview}
        </button>
      ) : (
        // Re-enable pointer events on the button wrapper so the round surface is
        // clickable while the row continues to ignore clicks around it.
        <div
          {...qaAttr({ id: 'community-scroll-down' })}
          style={{ pointerEvents: 'auto' }}
        >
          <ScrolledUpButton onClick={onScrollDown} />
        </div>
      )}
    </div>
  );
};
